/**
 * Client-side cache for the user's favorited listing IDs.
 *
 * Instead of each PropertyCard / Card firing an individual
 * `GET /favorites/{id}/check` on mount (which triggers 429s),
 * this module fetches ALL favorites once and lets every hook
 * read from the shared in-memory Set.
 *
 * Usage:
 *   await ensureFavoritesLoaded();   // one call, idempotent
 *   isCachedFavorite(listingId);     // sync read
 *   setCachedFavorite(id, true);     // optimistic update after toggle
 */

import { getAllFavorites } from "@/services/dwellFinderApi/favoritesActions";

/** The canonical set of favorited listing IDs. */
let favoriteIds: Set<string> = new Set();

/** Tracks whether a fetch is in progress so we don't fire duplicates. */
let inflightPromise: Promise<void> | null = null;

/** Whether we've successfully loaded at least once this session. */
let loaded = false;

/**
 * Ensure the favorites set has been fetched from the server.
 * Safe to call from many components concurrently — only one
 * network request will be made.
 */
export async function ensureFavoritesLoaded(): Promise<void> {
  if (loaded) return;
  if (inflightPromise) return inflightPromise;

  inflightPromise = (async () => {
    try {
      const response = await getAllFavorites();
      const items: any[] = Array.isArray(response)
        ? response
        : (response.data ?? response.favorites ?? []);

      favoriteIds = new Set(
        items.map((item: any) => {
          const listing = item.post ?? item.listing ?? item;
          return listing.id ?? listing._id ?? "";
        }).filter(Boolean),
      );
      loaded = true;
    } catch (err) {
      // Non-fatal — individual hooks will just default to false
      console.warn("[favoritesCache] Failed to load favorites:", err);
    } finally {
      inflightPromise = null;
    }
  })();

  return inflightPromise;
}

/** Synchronous lookup after the cache is warm. */
export function isCachedFavorite(listingId: string): boolean {
  return favoriteIds.has(listingId);
}

/** Optimistically update the cache after a toggle. */
export function setCachedFavorite(listingId: string, favorited: boolean): void {
  if (favorited) {
    favoriteIds.add(listingId);
  } else {
    favoriteIds.delete(listingId);
  }
}
